import { useMutation } from "@vue/apollo-composable";
import { computedEager } from "@vueuse/core";
import { omit, sumBy } from "lodash";
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";
import { addOrUpdateOrderPaymentWithTokenValidation } from "@/core/api/graphql/orders/mutations/addOrUpdateOrderPaymentWithTokenValidation";
import { AddOrUpdateOrderPaymentDocument } from "@/core/api/graphql/types";
import { useCurrency } from "@/core/composables";
import { useDataAreaId } from "@/core/composables/useDataAreaId";
import { AddressType } from "@/core/enums";
import { globals } from "@/core/globals";
import { isEqualAddresses } from "@/core/utilities/address";
import { Logger } from "@/core/utilities/logger";
import {
  mapToCheckoutItems,
  mapToCheckoutShipment,
  mapToCheckoutPaymentMethod,
  mapToCheckoutItemsFromOrder,
} from "@/core/utilities/orders";
import {
  create478PaymentComment,
  decodeJwtPayload,
  fromCardDetailsToPaymentOutputStateDetails,
} from "@/core/utilities/payments";
import { useUser, useUserOrder, useUserPayments } from "@/shared/account";
import { useFullCart } from "@/shared/cart";
import { useCountryISO3 } from "@/shared/common/composables/useCountryISO3";
import { useThorlabsGtm } from "@/shared/gtm/gtm";
import { collectBrowserData } from "@/shared/payment/composables/useBrowserData";
import { useDeviceDataCollection } from "@/shared/payment/composables/useDeviceDataCollection";
import { usePayerAuthentication } from "@/shared/payment/composables/usePayerAuthentication";
import {
  analyzeCyberSourceError,
  getCyberSourceErrorMessage,
  shouldReinitPaymentForm,
} from "@/shared/payment/utils/cybersource-error-handler";
import { eventBus } from "@/shared/thorlabs/shared";
import { currencyConfig } from "../currency-settings";
import { ORDER_ERROR_HANDLED } from "../types";
import { useCheckout } from "./useCheckout";
import type { CurrencySettingsType, OrderErrorHandledType } from "../types";
import type {
  InputAddOrUpdateOrderPaymentTypeExtension,
  MemberAddressTypeExtension,
  CreateOrderWithPaymentAuthorizationResultType,
  InputUpdateAccessoryType,
  AccessoryItemType,
  InputOrderPaymentTypeExtension,
  AuthorizePaymentResultType,
  AddOrUpdateOrderPaymentWithTokenValidationResultType,
  CartAddressTypeExtension,
  OrderAddressTypeExtension,
  PaymentInstrumentDetailsType,
} from "@/core/api/graphql/types";

export function useCheckoutFacade() {
  const { setBillingCountryCode, getDataAreaId } = useDataAreaId();
  const router = useRouter();
  const { t } = useI18n();
  const { setAuthorizationParameters } = useUserPayments();
  const { gtmSendAddPaymentInfoEventAsync } = useThorlabsGtm();
  const { waitForDeviceDataCollection } = useDeviceDataCollection();
  const { is3DSPayerAuthenticationEnabled } = usePayerAuthentication();

  const {
    checkoutMode,
    purchaseOrderNumber,
    purchaseOrderNumberUpdate,
    comment,
    wantsInvoicePdf,
    wantsProformaInvoice,
    intendsToExport,
    customerOrderComment,
    customerOrderWantsInvoicePdf,
    customerOrderWantsProformaInvoice,
    customerOrderPurchaseOrderNumber,
    customerOrderPurchaseOrderNumberUpdate,
    customerOrderIntendsToExport,
    deliveryAddress,
    billingAddress,
    quoteOrder,
    quoteOrderHasDiscounts,
    quoteOrderHasOnlyNewLabDiscount,
    customerOrderAvailabilityItems,
    isValidCheckout,
    updateUsdSalesTax,
    changing: checkoutChanging,
    loading: loadingCheckout,
    userSelectedAddress,
    addresses,
    userSelectedBillingAddress,
    setShippingMethod,
    shipmentMethodTopNote,
    shippingAccountNumberRecalculateTotals,
    hasTaxExemptNo,
    createOrderFromCart,
    createOrderFromQuote,
    createOrderFromCartWithPaymentAuthorization,
    createOrderFromQuoteWithPaymentAuthorization,
    isPaymentMethodExpanded,
    selectedPaymentMethod,
    hasCheckoutCompleted,
    hasCompletedExportStep,
    paymentMethodDetailsForReview,
    billingAddressEqualsShipping,
    isRequestQuoteFlow,
    initialize,
    initializeQuoteCheckout,
    placedOrder,
    setCartDeliveryAddress,
    waitForPendingCartWrites,
    deliveryAddressListUpdaterKey,
    stopLoading,
  } = useCheckout();
  const {
    payment,
    availabilityItems,
    cart,
    shipment,
    cartHasDiscounts,
    cartHasOnlyNewLabDiscount,
    cartWeight,
    cartHasUkHeavyItems,
    cartHasVaporCells,
    changing: cartChanging,
    loading: loadingCart,
    availableShippingMethods,
    updateAccessories,
    updatePayment,
    updatePaymentWithTokenValidation,
  } = useFullCart();
  const isCustomerOrder = computed(() => checkoutMode.value === "CustomerOrder");

  const checkoutItems = computed(() =>
    isCustomerOrder.value
      ? mapToCheckoutItemsFromOrder(customerOrderAvailabilityItems.value, isCustomerOrder.value)
      : mapToCheckoutItems(availabilityItems.value ?? [], isCustomerOrder.value),
  );

  const checkoutShipment = computed(() =>
    mapToCheckoutShipment(isCustomerOrder.value ? quoteOrder.value?.shipments?.[0] : shipment?.value ?? null),
  );

  const totalItems = computed(() => {
    const itemsLength = isCustomerOrder.value
      ? sumBy(quoteOrder.value?.items, (x) => x.quantity) ?? 0
      : sumBy(cart.value?.items, (x) => x.quantity) ?? 0;

    return itemsLength === 1
      ? `(${itemsLength} ${t("shared.cart.item")})`
      : `(${itemsLength} ${t("common.labels.items")})`;
  });

  const totalItemsWithLines = computed(() => {
    const linesCount = isCustomerOrder.value ? quoteOrder.value?.items?.length ?? 0 : cart.value?.items?.length ?? 0;
    const linesText = linesCount === 1 ? `1 ${t("shared.cart.line")}` : `${linesCount} ${t("shared.cart.lines")}`;

    const itemsText = totalItems.value.slice(1, -1);
    return `${itemsText} ${t("common.labels.on_lowercase")} ${linesText}`;
  });

  const { currentCurrency } = useCurrency();

  const formattedCartWeight = computed(() => {
    if (!cartWeight.value) {
      return null;
    }

    const isUSD = currentCurrency.value.code === "USD";
    const weight = isUSD ? cartWeight.value : cartWeight.value / 2.205;

    const formattedWeight = weight < 0.01 ? 0.01 : parseFloat(weight.toFixed(2));
    const unit = isUSD ? "lbs" : "kg";

    return `${formattedWeight} ${unit}`;
  });

  const subTotal = computed(() => (isCustomerOrder.value ? quoteOrder.value?.subTotal : cart.value?.subTotal));
  const cartSubtotalWithTax = computed(() => cart.value?.subTotalWithTax);
  const discountTotal = computed(() =>
    isCustomerOrder.value ? quoteOrder.value?.discountTotal : cart.value?.discountTotal,
  );
  const discountTotalWithTax = computed(() =>
    isCustomerOrder.value ? quoteOrder.value?.discountTotalWithTax : cart.value?.discountTotalWithTax,
  );
  const handlingTotal = computed(() =>
    isCustomerOrder.value ? quoteOrder.value?.handlingTotal : cart.value?.handlingTotal,
  );
  const taxTotal = computed(() => (isCustomerOrder.value ? quoteOrder.value?.taxTotal : cart.value?.taxTotal));
  const total = computed(() => (isCustomerOrder.value ? quoteOrder.value?.total : cart.value?.total));
  const hasDiscounts = computed(() => (isCustomerOrder.value ? quoteOrderHasDiscounts.value : cartHasDiscounts.value));
  const hasOnlyNewLabDiscount = computed(() =>
    isCustomerOrder.value ? quoteOrderHasOnlyNewLabDiscount.value : cartHasOnlyNewLabDiscount.value,
  );
  const availablePaymentMethods = computed(() => {
    const currencySettings = currencyConfig[globals.currencyCode] as CurrencySettingsType;
    const paymentMethods = isCustomerOrder.value
      ? quoteOrder.value?.availablePaymentMethods
      : cart.value?.availablePaymentMethods;

    // Return empty array if cart/order is not loaded yet
    if (!paymentMethods) {
      return [];
    }

    const mappedPaymentMethods = mapToCheckoutPaymentMethod(paymentMethods);
    return mappedPaymentMethods.filter((method) => currencySettings.availablePaymentMethods.includes(method.code));
  });

  const checkoutPaymentMethod = computed(() => {
    const paymentMethod = isCustomerOrder.value
      ? quoteOrder.value?.inPayments?.[0]?.gatewayCode
      : payment.value?.paymentGatewayCode;

    return availablePaymentMethods.value.find((method) => method.code === paymentMethod)?.code;
  });

  // Validation computed properties for customerOrder (similar to cart validations)
  const isValidCustomerOrderDeliveryAddress = computed<boolean>(
    () => !!quoteOrder.value?.shipments?.[0]?.deliveryAddress,
  );

  const isValidCustomerOrderBillingAddress = computed<boolean>(
    () => !!quoteOrder.value?.inPayments?.[0]?.billingAddress,
  );

  const isValidCustomerOrderShipmentMethod = computed<boolean>(
    () => !!quoteOrder.value?.shipments?.[0]?.shipmentMethodCode,
  );

  const isValidCustomerOrderPaymentMethod = computed<boolean>(() => !!quoteOrder.value?.inPayments?.[0]?.gatewayCode);

  const isValidCustomerOrderShipment = computed<boolean>(
    () => isValidCustomerOrderDeliveryAddress.value && isValidCustomerOrderShipmentMethod.value,
  );

  const isValidCustomerOrderPayment = computed<boolean>(
    () => isValidCustomerOrderBillingAddress.value && isValidCustomerOrderPaymentMethod.value,
  );

  const isPORequired = computed(() => {
    return (
      !isCustomerOrder.value &&
      selectedPaymentMethod.value === "NetTerms" &&
      (!purchaseOrderNumber.value || purchaseOrderNumber.value.trim().length === 0)
    );
  });

  const isPORequiredForQuote = computed(() => {
    return (
      isCustomerOrder.value &&
      selectedPaymentMethod.value === "NetTerms" &&
      (!customerOrderPurchaseOrderNumber.value || customerOrderPurchaseOrderNumber.value.trim().length === 0)
    );
  });
  const isValidCheckoutFinal = computed<boolean>(() => {
    if (isCustomerOrder.value) {
      return isValidCustomerOrderShipment.value && isValidCustomerOrderPayment.value;
    } else {
      return isValidCheckout.value;
    }
  });

  // Add the addOrUpdateOrderPayment function
  const { mutate: addOrUpdateOrderPaymentMutation } = useMutation(AddOrUpdateOrderPaymentDocument);
  const { fetchFullOrder } = useUserOrder();
  const { authorizationParameters } = useUserPayments();

  async function addOrUpdateOrderPayment(payload: InputAddOrUpdateOrderPaymentTypeExtension) {
    const result = await addOrUpdateOrderPaymentMutation({
      command: payload,
    });

    // Refresh quoteOrder with the updated data after payment update
    if (result?.data?.addOrUpdateOrderPayment) {
      // Fetch the updated order to refresh quoteOrder state
      await fetchFullOrder({ id: payload.orderId });
    }

    return result?.data?.addOrUpdateOrderPayment;
  }

  async function updateCartAccessories(accessories: AccessoryItemType[]) {
    const input = {
      storeId: globals.storeId,
      currencyCode: globals.currencyCode,
      cultureName: globals.cultureName,
      cartId: cart.value?.id,
      userId: globals.userId,
      accessoryItems: accessories,
    } as InputUpdateAccessoryType;

    await updateAccessories(input);
  }

  // Main order creation method that handles both cart and quote orders
  async function createOrder(callback: (result?: unknown) => void) {
    if (checkoutPaymentMethod.value === "CreditCard") {
      if (isCustomerOrder.value) {
        await createOrderFromQuoteWithAuthorization(callback);
      } else {
        await createOrderWithAuthorization(callback);
      }
    } else {
      try {
        if (isCustomerOrder.value) {
          await createOrderFromQuote();
        } else {
          await createOrderFromCart();
        }
      } finally {
        callback();
      }
    }
  }

  async function createOrderWithAuthorization(callback: (result?: unknown) => void) {
    if (authorizationParameters.value?.length > 0) {
      // Append browser data fields as backup for DDC only when 3DS is enabled
      const billingCountryCode = useCountryISO3(billingAddress.value?.countryCode ?? "") ?? "";
      const dataAreaId = getDataAreaId(currentCurrency.value.code, billingCountryCode);
      const shouldAttachBrowserData = is3DSPayerAuthenticationEnabled(dataAreaId);

      // When 3DS payer authentication is enabled, ensure Device Data Collection has completed before the enrollment check
      if (shouldAttachBrowserData) {
        await waitForDeviceDataCollection();
      }

      const parametersCollection = shouldAttachBrowserData
        ? [...authorizationParameters.value, ...collectBrowserData()]
        : authorizationParameters.value;

      await createOrderFromCartWithPaymentAuthorization(parametersCollection)
        .then(async (value: CreateOrderWithPaymentAuthorizationResultType | OrderErrorHandledType | null) => {
          if (value === ORDER_ERROR_HANDLED) {
            // The error was already reported to the user — skip payment-error handling to avoid a conflicting error UI.
            return;
          }
          if (!value?.isSuccess) {
            if (value?.accessToken && value?.stepUpUrl) {
              if (value?.authenticationTransactionId) {
                setAuthorizationParameters([
                  ...authorizationParameters.value.filter((param) => param.key !== "authenticationTransactionId"),
                  {
                    key: "authenticationTransactionId",
                    value: value.authenticationTransactionId ?? "",
                  },
                ]);
              }
              eventBus.emit("showStepUpAuthentication", {
                accessToken: value.accessToken,
                stepUpUrl: value.stepUpUrl,
                returnUrl: value.returnUrl ?? undefined,
                authenticationTransactionId: value.authenticationTransactionId ?? undefined,
              });
              return;
            } else {
              await handlePaymentAuthorizationError(value?.errorMessage);
            }
          } else {
            await router.replace({ name: "CheckoutCompleted" });
          }
        })
        .catch(async (e) => {
          await handlePaymentAuthorizationError(e?.message);
        });
    } else {
      await handlePaymentAuthorizationError();
    }
    callback();
  }

  async function createOrderFromQuoteWithAuthorization(callback: (result?: unknown) => void) {
    if (authorizationParameters.value?.length > 0) {
      // Append browser data fields as backup for DDC only when 3DS is enabled
      const dataAreaId = getDataAreaId(currentCurrency.value.code, billingAddress.value?.countryCode ?? "");
      const shouldAttachBrowserData = is3DSPayerAuthenticationEnabled(dataAreaId);

      // Ensure Device Data Collection has completed before the enrollment check
      if (shouldAttachBrowserData) {
        await waitForDeviceDataCollection();
      }

      const parametersCollection = shouldAttachBrowserData
        ? [...authorizationParameters.value, ...collectBrowserData()]
        : authorizationParameters.value;

      await createOrderFromQuoteWithPaymentAuthorization(parametersCollection)
        .then(async (value: CreateOrderWithPaymentAuthorizationResultType | null) => {
          if (!value?.isSuccess) {
            if (value?.accessToken && value?.stepUpUrl) {
              if (value?.authenticationTransactionId) {
                setAuthorizationParameters([
                  ...authorizationParameters.value.filter((param) => param.key !== "authenticationTransactionId"),
                  {
                    key: "authenticationTransactionId",
                    value: value.authenticationTransactionId ?? "",
                  },
                ]);
              }
              eventBus.emit("showStepUpAuthentication", {
                accessToken: value.accessToken,
                stepUpUrl: value.stepUpUrl,
                returnUrl: value.returnUrl ?? undefined,
                authenticationTransactionId: value.authenticationTransactionId ?? undefined,
              });
              return;
            } else {
              await handlePaymentAuthorizationError(value?.errorMessage);
            }
          } else {
            await router.replace({ name: "CheckoutCompleted" });
          }
        })
        .catch(async (e) => {
          await handlePaymentAuthorizationError(e?.message);
        });
    } else {
      await handlePaymentAuthorizationError();
    }
    callback();
  }

  async function handlePaymentAuthorizationError(message?: string) {
    Logger.error("payment authorization error:", message);
    const is478Error = message?.includes("478-ERROR");
    if (is478Error && globals.convertCc478OrdersToNetTerms) {
      const { user } = useUser();
      if (isCustomerOrder.value) {
        if (!customerOrderPurchaseOrderNumber.value) {
          customerOrderPurchaseOrderNumber.value = user.value?.contact?.oldContactIdTL || "";
        }
        if (customerOrderPurchaseOrderNumberUpdate.value) {
          await customerOrderPurchaseOrderNumberUpdate.value;
        }
        await nextStepHandle(() => Promise.resolve(), "NetTerms", true);
        await createOrderFromQuote();
      } else {
        if (!purchaseOrderNumber.value) {
          purchaseOrderNumber.value = user.value?.contact?.oldContactIdTL || "";
        }
        if (purchaseOrderNumberUpdate.value) {
          await purchaseOrderNumberUpdate.value;
        }
        await nextStepHandle(() => Promise.resolve(), "NetTerms", true);
        await createOrderFromCart();
      }
      return;
    }

    const errorResult = analyzeCyberSourceError(message);
    const hasPaymentToken = !!authorizationParameters.value?.find((x) => x.key === "paymentInstrumentTokenId");

    eventBus.emit("expandCheckoutComponent", {
      initPaymentForm: shouldReinitPaymentForm(errorResult) ? !hasPaymentToken : false,
    });

    const errorMessage = getCyberSourceErrorMessage(
      errorResult,
      t,
      "shared.payment.bank_card_form.default_authorization_error_checkout",
    );
    eventBus.emit("showCreditCardFormError", { message: errorMessage });
  }

  async function updatePaymentWithAdaptedBillingAddress(
    address: MemberAddressTypeExtension | CartAddressTypeExtension,
    selectedPaymentMethodType: string,
    is478ErrorHandled: boolean = false,
  ) {
    try {
      if (isCustomerOrder.value) {
        // Use addOrUpdateOrderPayment for acceptQuote flow
        await addOrUpdateOrderPayment({
          orderId: quoteOrder.value!.id!,
          payment: {
            id: quoteOrder.value?.inPayments.find((x) => x.gatewayCode !== "Quote")?.id ?? undefined,
            paymentGatewayCode: selectedPaymentMethodType,
            comment: is478ErrorHandled ? create478PaymentComment(authorizationParameters.value) : undefined,
            billingAddress: {
              ...omit(address, [
                "id",
                "isDefault",
                "description",
                "isFavorite",
                "isDefaultShippingAddressTL",
                "isDefaultBillingAddressTL",
                "modifiedDate",
                "createdDate",
              ]),
              outerId: address.id,
              addressType: AddressType.Billing,
            },
          },
        });
      } else {
        // Use updatePayment for regular checkout
        await updatePayment({
          id: payment.value?.id,
          paymentGatewayCode: selectedPaymentMethodType,
          comment: is478ErrorHandled ? create478PaymentComment(authorizationParameters.value) : undefined,
          billingAddress: {
            ...omit(address, [
              "id",
              "isDefault",
              "description",
              "isFavorite",
              "isDefaultShippingAddressTL",
              "isDefaultBillingAddressTL",
              "modifiedDate",
              "createdDate",
            ]),
            outerId: address.id,
            addressType: AddressType.Billing,
          },
        });
      }

      if (!is478ErrorHandled) {
        isPaymentMethodExpanded.value = false;
        hasCheckoutCompleted.value = true;
        setBillingCountryCode(
          address.countryCode?.length === 3 ? address.countryCode : useCountryISO3(address.countryCode ?? ""),
        );
      }
    } catch (error) {
      // Cart payment failures are already surfaced by updatePayment's notification.
      if (isCustomerOrder.value || selectedPaymentMethodType === "CreditCard") {
        eventBus.emit("showCreditCardFormError", {
          message: t("shared.payment.bank_card_form.default_authorization_error_checkout"),
        });
      }
    }
  }

  async function updateOrderPaymentWithTokenValidation(
    value: InputOrderPaymentTypeExtension,
    token: string,
    kid: string,
  ): Promise<AuthorizePaymentResultType> {
    try {
      const response: AddOrUpdateOrderPaymentWithTokenValidationResultType =
        await addOrUpdateOrderPaymentWithTokenValidation({
          orderId: quoteOrder.value!.id!,
          payment: value,
          token: token,
          kid: kid,
        });

      // Refresh quoteOrder with the updated data after payment update
      if (response.isSuccess && response.order) {
        // Fetch the updated order to refresh quoteOrder state
        await fetchFullOrder({ id: quoteOrder.value!.id! });
      }

      return {
        isSuccess: response.isSuccess,
        errorMessage: response.errorMessage || undefined,
      };
    } catch (error) {
      return {
        isSuccess: false,
        errorMessage: "Failed to update order payment",
      };
    }
  }

  async function updatePaymentWithTokenValidationFacade(
    address: MemberAddressTypeExtension | CartAddressTypeExtension,
    selectedPaymentMethodType: string,
    token: string,
    kid: string,
  ): Promise<AuthorizePaymentResultType> {
    try {
      if (isCustomerOrder.value) {
        // Use updateOrderPaymentWithTokenValidation for acceptQuote flow
        const result = await updateOrderPaymentWithTokenValidation(
          {
            id: undefined,
            paymentGatewayCode: selectedPaymentMethodType,
            billingAddress: {
              ...omit(address, [
                "id",
                "isDefault",
                "description",
                "isFavorite",
                "isDefaultShippingAddressTL",
                "isDefaultBillingAddressTL",
                "modifiedDate",
                "createdDate",
              ]),
              addressType: AddressType.Billing,
            },
          },
          token,
          kid,
        );

        if (result.isSuccess) {
          isPaymentMethodExpanded.value = false;
          hasCheckoutCompleted.value = true;
          setBillingCountryCode(
            address.countryCode?.length === 3 ? address.countryCode : useCountryISO3(address.countryCode ?? ""),
          );
        }

        return result;
      } else {
        // Use updatePaymentWithTokenValidation for regular checkout
        const result = await updatePaymentWithTokenValidation(
          {
            id: payment.value?.id,
            paymentGatewayCode: selectedPaymentMethodType,
            billingAddress: {
              ...omit(address, [
                "id",
                "isDefault",
                "description",
                "isFavorite",
                "isDefaultShippingAddressTL",
                "isDefaultBillingAddressTL",
                "modifiedDate",
                "createdDate",
              ]),
              addressType: AddressType.Billing,
            },
          },
          token,
          kid,
        );

        if (result.isSuccess) {
          isPaymentMethodExpanded.value = false;
          hasCheckoutCompleted.value = true;
          setBillingCountryCode(
            address.countryCode?.length === 3 ? address.countryCode : useCountryISO3(address.countryCode ?? ""),
          );
        }

        return result;
      }
    } catch (error) {
      return {
        isSuccess: false,
        errorMessage: "Failed to update payment with token validation",
      };
    }
  }

  async function updatePaymentAndProceed(
    address: MemberAddressTypeExtension | CartAddressTypeExtension | OrderAddressTypeExtension,
    selectedPaymentMethodType: string,
    savedCardDetails?: PaymentInstrumentDetailsType | null,
    savedCardGetParameters?: ((dataAreaId: string) => Array<{ key: string; value: string }> | undefined) | null,
    createTokenWithValidationPayload?:
      | ((address: MemberAddressTypeExtension | CartAddressTypeExtension | OrderAddressTypeExtension) => Promise<
          | {
              token?: string;
              kid?: string;
              cardholderName?: string;
            }
          | undefined
        >)
      | null,
    setAsDefaultPaymentCard?: boolean | null,
    newCardGetParameters?:
      | ((
          maskedCardNumber: string,
          cardType: string,
          dataAreaId?: string,
        ) => Array<{ key: string; value?: string }> | undefined)
      | null,
    is478ErrorHandled: boolean = false,
  ) {
    if (selectedPaymentMethodType === "CreditCard") {
      if (savedCardDetails) {
        paymentMethodDetailsForReview.value = fromCardDetailsToPaymentOutputStateDetails(savedCardDetails);
        await updatePaymentWithAdaptedBillingAddress(address, selectedPaymentMethodType, is478ErrorHandled);
        const dataAreaId = getDataAreaId(currentCurrency.value.code, address.countryCode ?? "");
        const savedCreditCardParameters = savedCardGetParameters?.(dataAreaId);
        if (savedCreditCardParameters) {
          setAuthorizationParameters([
            ...savedCreditCardParameters,
            {
              key: "billingAddressId",
              value: savedCardDetails.billingAddress?.id ?? "",
            },
          ]);
        }
      } else {
        const tokenValidationPayload = await createTokenWithValidationPayload?.(address);
        if (tokenValidationPayload?.token && tokenValidationPayload?.kid) {
          try {
            const result = await updatePaymentWithTokenValidationFacade(
              address,
              selectedPaymentMethodType,
              tokenValidationPayload.token,
              tokenValidationPayload.kid,
            );
            if (!result?.isSuccess) {
              eventBus.emit("showCreditCardFormError", {
                message: t("shared.payment.bank_card_form.default_authorization_error_checkout"),
              });
              return;
            } else {
              paymentMethodDetailsForReview.value = decodeJwtPayload(
                tokenValidationPayload.token,
                tokenValidationPayload.cardholderName ?? "",
                setAsDefaultPaymentCard ?? false,
              );
              const dataAreaId = getDataAreaId(currentCurrency.value.code, address.countryCode ?? "");
              const newCreditCardParameters = newCardGetParameters?.(
                paymentMethodDetailsForReview.value.maskedCardNumber ?? "",
                paymentMethodDetailsForReview.value.cardType ?? "",
                dataAreaId,
              );
              if (newCreditCardParameters) {
                setAuthorizationParameters([
                  ...newCreditCardParameters,
                  {
                    key: "billingAddressId",
                    value: address.id ?? "",
                  },
                ]);
              }
            }
          } catch (error) {
            eventBus.emit("showCreditCardFormError", {
              message: t("shared.payment.bank_card_form.default_authorization_error_checkout"),
            });
            return;
          }
        } else {
          return;
        }
      }
    } else {
      await updatePaymentWithAdaptedBillingAddress(address, selectedPaymentMethodType, is478ErrorHandled);
    }
  }

  const nextStepHandle = async (
    callback: (result: unknown) => void,
    selectedPaymentMethodType: string,
    is478ErrorHandled: boolean = false,
    savedCardDetails?: PaymentInstrumentDetailsType | null,
    savedCardGetParameters?: ((dataAreaId: string) => Array<{ key: string; value: string }> | undefined) | null,
    createTokenWithValidationPayload?:
      | ((address: MemberAddressTypeExtension | CartAddressTypeExtension | OrderAddressTypeExtension) => Promise<
          | {
              token?: string;
              kid?: string;
              cardholderName?: string;
            }
          | undefined
        >)
      | null,
    setAsDefaultPaymentCard?: boolean | null,
    newCardGetParameters?:
      | ((
          maskedCardNumber: string,
          cardType: string,
          dataAreaId?: string,
        ) => Array<{ key: string; value?: string }> | undefined)
      | null,
  ) => {
    try {
      if (!isCustomerOrder.value) {
        await waitForPendingCartWrites();
      }

      // Save shipping address as billing address
      if (billingAddressEqualsShipping.value && !savedCardDetails) {
        const deliveryAddressWithId = addresses.value.find((item) =>
          isEqualAddresses(item, Object.assign({}, deliveryAddress.value)),
        );
        await updatePaymentAndProceed(
          deliveryAddressWithId ?? deliveryAddress.value!,
          selectedPaymentMethodType,
          savedCardDetails,
          savedCardGetParameters,
          createTokenWithValidationPayload,
          setAsDefaultPaymentCard,
          newCardGetParameters,
          is478ErrorHandled,
        );
      } else if (userSelectedBillingAddress.value && !savedCardDetails) {
        const selectedAddress = addresses.value.find((address) => address.id === userSelectedBillingAddress.value);
        selectedAddress!.dataAreaIdTL = getDataAreaId(
          currentCurrency.value.code,
          selectedAddress!.countryCode!,
          selectedPaymentMethodType,
        );
        await updatePaymentAndProceed(
          selectedAddress!,
          selectedPaymentMethodType,
          savedCardDetails,
          savedCardGetParameters,
          createTokenWithValidationPayload,
          setAsDefaultPaymentCard,
          newCardGetParameters,
          is478ErrorHandled,
        );
      } else if (savedCardDetails?.billingAddress) {
        userSelectedBillingAddress.value = savedCardDetails.billingAddress.id;
        const selectedAddress = addresses.value.find((address) => address.id === userSelectedBillingAddress.value);
        selectedAddress!.dataAreaIdTL = getDataAreaId(
          currentCurrency.value.code,
          selectedAddress!.countryCode!,
          selectedPaymentMethodType,
        );
        await updatePaymentAndProceed(
          selectedAddress!,
          selectedPaymentMethodType,
          savedCardDetails,
          savedCardGetParameters,
          createTokenWithValidationPayload,
          setAsDefaultPaymentCard,
          newCardGetParameters,
          is478ErrorHandled,
        );
      }

      await gtmSendAddPaymentInfoEventAsync(selectedPaymentMethodType);
    } finally {
      callback(undefined);
    }
  };

  return {
    poNumber: computed({
      get: () => (isCustomerOrder.value ? customerOrderPurchaseOrderNumber.value : purchaseOrderNumber.value),
      set: (v: string) =>
        isCustomerOrder.value ? (customerOrderPurchaseOrderNumber.value = v) : (purchaseOrderNumber.value = v),
    }),
    commentText: computed({
      get: () => (isCustomerOrder.value ? customerOrderComment.value : comment.value),
      set: (v: string) => (isCustomerOrder.value ? (customerOrderComment.value = v) : (comment.value = v)),
    }),
    invoicePdf: computed({
      get: () => (isCustomerOrder.value ? customerOrderWantsInvoicePdf.value : wantsInvoicePdf.value),
      set: (v: boolean) =>
        isCustomerOrder.value ? (customerOrderWantsInvoicePdf.value = v) : (wantsInvoicePdf.value = v),
    }),
    proformaInvoice: computed({
      get: () => (isCustomerOrder.value ? customerOrderWantsProformaInvoice.value : wantsProformaInvoice.value),
      set: (v: boolean) =>
        isCustomerOrder.value ? (customerOrderWantsProformaInvoice.value = v) : (wantsProformaInvoice.value = v),
    }),
    intendsToExport: computed({
      get: () => (isCustomerOrder.value ? customerOrderIntendsToExport.value : intendsToExport.value),
      set: (v: boolean) =>
        isCustomerOrder.value ? (customerOrderIntendsToExport.value = v) : (intendsToExport.value = v),
    }),
    deliveryAddress: computed(() =>
      isCustomerOrder.value ? quoteOrder.value?.shipments?.[0]?.deliveryAddress : deliveryAddress.value,
    ),
    billingAddress: computed(() =>
      isCustomerOrder.value ? quoteOrder.value?.inPayments?.[0]?.billingAddress : billingAddress.value,
    ),
    payment: computed(() => (isCustomerOrder.value ? quoteOrder.value?.inPayments?.[0] : payment.value)),
    quoteOrder,
    cartSubtotalWithTax,
    taxTotal,
    handlingTotal,
    discountTotal,
    discountTotalWithTax,
    total,
    hasDiscounts,
    hasOnlyNewLabDiscount,
    isCustomerOrder,
    availablePaymentMethods,
    addOrUpdateOrderPayment,
    orderId: computed(() => quoteOrder.value?.id),
    isValidCheckoutFinal,
    isPORequired,
    isPORequiredForQuote,
    checkoutItems,
    checkoutShipment,
    totalItems,
    totalItemsWithLines,
    cart,
    cartId: computed(() => cart.value?.id),
    subTotal,
    changing: computedEager(() => cartChanging.value || checkoutChanging.value),
    loading: computedEager(() => loadingCart.value || loadingCheckout.value),

    userSelectedAddress,
    addresses,
    userSelectedBillingAddress,

    // shipping method
    cartWeight,
    formattedCartWeight,
    cartHasUkHeavyItems,
    cartHasVaporCells,
    cartAvailableShippingMethods: computed(() => availableShippingMethods.value),
    shipmentMethodTopNote,

    // actions
    setCartDeliveryAddress,
    deliveryAddressListUpdaterKey,
    setShippingMethod,
    shippingAccountNumberRecalculateTotals,
    updateUsdSalesTax,
    updateCartAccessories,

    // order summary
    hasTaxExemptNo,

    // place order
    checkoutPaymentMethod,
    isRequestQuoteFlow,

    // create order
    createOrder,

    // payment
    isPaymentMethodExpanded,
    selectedPaymentMethod,
    hasCheckoutCompleted,
    hasCompletedExportStep,
    paymentMethodDetailsForReview,
    billingAddressEqualsShipping,
    updatePaymentWithAdaptedBillingAddress,
    updatePaymentWithTokenValidation,
    updatePaymentWithTokenValidationFacade,
    updatePaymentAndProceed,
    nextStepHandle,

    // initialization
    initialize,
    initializeQuoteCheckout,
    placedOrder,
    authorizationParameters,
    stopLoading,
  };
}
